import { FadeIn } from '@/components/PartFinder/FadeIn';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

const Frame = styled(Box)({
  overflow: 'hidden',
  transition: 'height 400ms cubic-bezier(0.4, 0, 0.2, 1)',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

const Content = styled(Box)({
  display: 'flow-root',
});

const NO_ANIMATION = { animation: 'none' };

const useAnimatedHeight = (animating: boolean) => {
  const contentReference = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const content = contentReference.current;
    if (!animating || !content) {
      setHeight(undefined);
      return;
    }
    const observer = new ResizeObserver(() => setHeight(content.offsetHeight));
    observer.observe(content);
    return () => observer.disconnect();
  }, [animating]);

  return { contentReference, height };
};

const AppearingContent = ({ animate, children }: { animate: boolean; children: ReactNode }) => {
  const [animateOnMount] = useState(animate);
  return <FadeIn sx={animateOnMount ? undefined : NO_ANIMATION}>{children}</FadeIn>;
};

export const RevealSection = ({
  show = true,
  animating,
  children,
}: {
  show?: boolean;
  animating: boolean;
  children: ReactNode;
}) => {
  const { contentReference, height } = useAnimatedHeight(animating);
  return (
    <Frame style={{ height }}>
      <Content ref={contentReference}>
        {show && <AppearingContent animate={animating}>{children}</AppearingContent>}
      </Content>
    </Frame>
  );
};
